import { Box, Button, Grid, IconButton, InputAdornment, Pagination, TextField, Typography, useMediaQuery } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { AppDispatch } from "../../redux/store";
import { getAllBreefs } from "../../service/BreefService";
import { getAllTeams } from "../../service/teamService";
import { useAddTenantForm } from "../../utils/forms";
import BreefTable, { Result } from "../manageBreef/BreefTable";
import { useTranslation } from "react-i18next";
import { updateAdminDashUser, updateTenantAccount, TenantAccountValues } from "../../service/UserService";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TeamTable, { TeamResult } from "../../manageTeam/TeamTable";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import { getTenant } from "../../service/TenantService";
import TenantAgreements from "./TenantAgreements";
import TenantInsightAccess from "./TenantInsightAccess";

const isSupportAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").adminRole === "support";
  } catch {
    return false;
  }
};

const UpdateViewEditTenant = () => {
  const [t] = useTranslation("global");
  const location = useLocation();
  const { tenantId } = useParams();
  const { record } = location.state || "";
  // The id in the URL is the source of truth, so the page also works after a reload or from a link
  const userId: string = tenantId || location.state?.userId || "";
  const isEdit = new URLSearchParams(location.search).get("isEdit");
  const isMobile = useMediaQuery("(max-width:600px)");
  const formik = useAddTenantForm(!!isEdit, userId || "", isMobile);
  const [isEditing, setIsEditing] = useState(isEdit === "true");
  const [filteredData, setFilteredData] = useState<Result[]>([]);
  const [teamData, setTeamData] = useState<TeamResult[]>([]);

  const [teamCurrentPage, setTeamCurrentPage] = useState(1);
  const [breefCurrentPage, setBreefCurrentPage] = useState(1);
  const [pageLimit] = useState(10);
  const [totalPages] = useState(0);
  const [totalResults] = useState(0);
  const [rowsPerPage] = useState(10);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  // Email, phone and password are saved through the admin account endpoint (see updateTenantAccount)
  const [account, setAccount] = useState({ email: record?.email || "", phoneNumber: record?.phoneNumber || "" });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountErrors, setAccountErrors] = useState<Record<string, string>>({});
  const [savedName, setSavedName] = useState({ firstName: record?.firstName || "", lastName: record?.lastName || "" });

  const fetchBreefs = async (pageNo: number) => {
    const data = await getAllBreefs(dispatch, isMobile, navigate, userId, pageLimit, pageNo);
    if (data) {
      console.log("Data: ", data);

      setFilteredData(data.results);
    }
  };

  const fetchTeams = async (pageNo: number) => {
    const data = await getAllTeams(isMobile, navigate, userId,pageNo,rowsPerPage);
    if (data) {
      console.log("Team Data: ", data);

      setTeamData(data.teamList);
    }
  };

  useEffect(() => {
    console.log("Fetchign data");

    if (userId) {
      fetchBreefs(breefCurrentPage);
      fetchTeams(teamCurrentPage);
    }
  }, [userId, teamCurrentPage,breefCurrentPage]);

  const handleTeamPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    console.log("page val: ", value);

    setTeamCurrentPage(value);
  };
  
  const handleBreefPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    console.log("page val: ", value);

    setBreefCurrentPage(value);
  };

  const generatePassword = () => {
    const letters = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
    const digits = "23456789";
    const random = (chars: string) => chars[crypto.getRandomValues(new Uint32Array(1))[0] % chars.length];
    const password = random(letters) + random(digits) + Array.from({ length: 10 }, () => random(letters + digits)).join("");
    setNewPassword(password);
    setConfirmPassword(password);
    setShowPassword(true);
  };

  // Only the fields that actually changed are sent, and the same rules as the backend are checked first
  const getAccountChanges = (): TenantAccountValues | null => {
    const errors: Record<string, string> = {};
    const changes: TenantAccountValues = {};
    const email = (formik.values.email || "").trim().toLowerCase();
    const phoneNumber = (formik.values.phoneNumber || "").trim();
    if (email !== (account.email || "").toLowerCase()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t("updateTenant.account.emailInvalid");
      else changes.email = email;
    }
    if (phoneNumber !== (account.phoneNumber || "")) {
      if (!/^\+?[0-9 ]{8,20}$/.test(phoneNumber)) errors.phoneNumber = t("updateTenant.account.phoneInvalid");
      else changes.phoneNumber = phoneNumber;
    }
    if (newPassword || confirmPassword) {
      if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(newPassword)) errors.password = t("updateTenant.account.passwordRule");
      else if (newPassword !== confirmPassword) errors.confirm = t("updateTenant.account.passwordMismatch");
      else changes.password = newPassword;
    }
    setAccountErrors(errors);
    return Object.keys(errors).length ? null : changes;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const accountChanges = getAccountChanges();
    if (!accountChanges) return;
    try {
      const data = {
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
      };
      const nameResponse = await updateAdminDashUser(userId, data);
      if (!nameResponse) {
        showErrorToast(t("updateTenant.account.saveFailed"), isMobile);
        return;
      }
      setSavedName(data);
      if (Object.keys(accountChanges).length) {
        const tenant = await updateTenantAccount(userId, accountChanges, isMobile, navigate);
        if (!tenant) return; // error toast already shown; keep the form open
        setAccount({ email: tenant.email, phoneNumber: tenant.phoneNumber });
        formik.setFieldValue("email", tenant.email);
        formik.setFieldValue("phoneNumber", tenant.phoneNumber);
        setNewPassword("");
        setConfirmPassword("");
      }
      // Stay on the form, so further changes don't need a trip back to Manage Tenants
      showSuccessToast(t("updateTenant.account.saved"), isMobile);
    } catch (err) {
      console.error("Error editing user details: ", err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    navigate("/manage-tenant");
  };

  const setUptenant = () => {
    // From the last saved values (not the list row, which is stale after a save)
    formik.setValues({
      firstName: savedName.firstName,
      lastName: savedName.lastName,
      password: "",
      email: account.email,
      phoneNumber: account.phoneNumber,
      role: record?.role,
    });
  };
  useEffect(() => {
    setUptenant();
  }, [userId, isEdit]);

  // Load the tenant from the API; the list row passed in the navigation state can be out of date
  useEffect(() => {
    if (!userId) return;
    getTenant(userId, isMobile, navigate).then((tenant) => {
      if (!tenant) return;
      setSavedName({ firstName: tenant.firstName || "", lastName: tenant.lastName || "" });
      setAccount({ email: tenant.email || "", phoneNumber: tenant.phoneNumber || "" });
      formik.setValues({
        firstName: tenant.firstName || "",
        lastName: tenant.lastName || "",
        password: "",
        email: tenant.email || "",
        phoneNumber: tenant.phoneNumber || "",
        role: tenant.role,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <Layout>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          marginRight: "10%",
          marginTop: "2%",
          marginBottom: "1%",
        }}
      >
        <Button onClick={handleCancel} startIcon={<ArrowBackIcon />} sx={{ mr: 2 }}>
          {t("common.back")}
        </Button>
        <Typography
          variant="h1"
          component="h1"
          style={{ display: "flex", alignItems: "center",  }}
        >
          {isEditing ? t("common.edit") : t("common.view")} {t("updateTenant.t1")}: {savedName.firstName} {savedName.lastName}
        </Typography>
      </div>
      <Box sx={{ display: "flex", flexDirection: "column", marginX: "10%", marginBottom: "5%" }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                required
                margin="dense"
                id="firstName"
                name="firstName"
                label={t("updateTenant.t2")}
                type="text"
                fullWidth
                variant="outlined"
                onChange={formik.handleChange("firstName")}
                value={formik.values.firstName}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="lastName"
                name="lastName"
                label={t("updateTenant.t3")}
                type="text"
                fullWidth
                variant="outlined"
                onChange={formik.handleChange("lastName")}
                value={formik.values.lastName}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                margin="dense"
                id="email"
                name="email"
                label={t("updateTenant.t4")}
                type="email"
                fullWidth
                variant="outlined"
                onChange={formik.handleChange("email")}
                value={formik.values.email}
                error={Boolean(accountErrors.email)}
                helperText={accountErrors.email}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                id="phoneNumber"
                name="phoneNumber"
                label={t("updateTenant.t5")}
                type="text"
                fullWidth
                variant="outlined"
                onChange={formik.handleChange("phoneNumber")}
                value={formik.values.phoneNumber}
                error={Boolean(accountErrors.phoneNumber)}
                helperText={accountErrors.phoneNumber}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
          {isEditing && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="newPassword"
                  label={t("updateTenant.account.label.password")}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  fullWidth
                  variant="outlined"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={Boolean(accountErrors.password)}
                  helperText={accountErrors.password || t("updateTenant.account.passwordHint")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((show) => !show)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  id="confirmPassword"
                  label={t("updateTenant.account.label.confirm")}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  fullWidth
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={Boolean(accountErrors.confirm)}
                  helperText={accountErrors.confirm}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="text" onClick={generatePassword} >
                  {t("updateTenant.account.generate")}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("updateTenant.account.intro")}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            {isEditing && (
              <>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    mr: 1,
                    "&:hover": { borderColor: "#d55a07" },
                  }}
                >
                  {t("updateTenant.t6")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ "&:hover": { backgroundColor: "#d55a07" } }}
                >
                  {t("updateTenant.t7")}
                </Button>
              </>
            )}
            {!isEditing && userId && (
              <Button
                onClick={() => {
                  setIsEditing(true);
                  navigate(`/tenant/${userId}?isEdit=true`, { replace: true, state: location.state });
                }}
                variant="contained"
                sx={{ "&:hover": { backgroundColor: "#d55a07" } }}
              >
                {t("updateTenant.account.edit")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Box>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            {t("nav.t11")}
          </AccordionSummary>
          <AccordionDetails>
            <TeamTable
              results={teamData}
              page={teamCurrentPage}
              limit={pageLimit}
              totalPages={totalPages}
              totalResults={totalResults}
            />
            <Box sx={{ display: "flex", justifyContent: "center", my: 2, alignItems: "center" }}>
              <Pagination
                count={totalPages + 2}
                page={teamCurrentPage}
                onChange={handleTeamPageChange}
                sx={{
                  "& .MuiPaginationItem-root": {
                  },
                  "& .Mui-selected": {
                    },
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3-content"
            id="panel3-header"
          >
            Breefs
          </AccordionSummary>
          <AccordionDetails>
            <BreefTable
              results={filteredData}
              page={breefCurrentPage}
              limit={pageLimit}
              totalPages={totalPages}
              totalResults={totalResults}
            />
            <Box sx={{ display: "flex", justifyContent: "center", my: 2, alignItems: "center" }}>
              <Pagination
                count={totalPages + 2}
                page={breefCurrentPage}
                onChange={handleBreefPageChange}
                sx={{
                  "& .MuiPaginationItem-root": {
                  },
                  "& .Mui-selected": {
                    },
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      {/* Terms / data processing agreement accepted by the tenant (superadmin; support gets 403 and sees nothing) */}
      {userId && !isSupportAdmin() && <TenantAgreements tenantId={userId} />}
      {/* Extra insight the tenant turned on (small teams / per person): status, and superadmin can turn it off with a reason */}
      {userId && <TenantInsightAccess tenantId={userId} canEdit={!isSupportAdmin()} />}
    </Layout>
  );
};

export default UpdateViewEditTenant;
